import { NEEDS } from "@/data/needs";
import { COUPLE_TIPS } from "@/data/couple";

const L = (lang, fr, es) => (lang === "es" ? es : fr);

const sumNeed = (measured, key) => {
  const arr = measured?.[key] || [];
  return arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0); // /20
};

const ALIGNED = 3; // |écart| <= 3 => aligné
const DIVERGENT = 7; // |écart| >= 7 => divergent

function statusOf(gap, lang) {
  const a = Math.abs(gap);
  if (a <= ALIGNED) return { key: "aligned", label: L(lang, "Aligné", "Alineado"), color: "#8A9A6D" };
  if (a >= DIVERGENT) return { key: "divergent", label: L(lang, "Divergent", "Divergente"), color: "#B0553B" };
  return { key: "moderate", label: L(lang, "Nuancé", "Matizado"), color: "#E99F4F" };
}

// p1/p2: { name, measured:{key:[..]}, selfEst:{key:0-20}, otherEst:{key:0-20} }
export function computeCouple(p1, p2, lang = "fr") {
  const rows = NEEDS.map((need) => {
    const m1 = sumNeed(p1.measured, need.key);
    const m2 = sumNeed(p2.measured, need.key);
    const gap = m1 - m2;
    return {
      key: need.key,
      name: need.name[lang],
      short: need.short[lang],
      m1,
      m2,
      gap,
      status: statusOf(gap, lang),
      // connaissance de l'autre
      p1GuessOfP2: p1.otherEst?.[need.key] ?? 0,
      p2GuessOfP1: p2.otherEst?.[need.key] ?? 0,
      p1KnowErr: Math.abs((p1.otherEst?.[need.key] ?? 0) - m2),
      p2KnowErr: Math.abs((p2.otherEst?.[need.key] ?? 0) - m1),
      p1Err: (p1.otherEst?.[need.key] ?? 0) - m2, // signé : >0 = p1 surestime p2
      p2Err: (p2.otherEst?.[need.key] ?? 0) - m1, // signé : >0 = p2 surestime p1
    };
  });

  const meanErr1 = rows.reduce((s, r) => s + r.p1KnowErr, 0) / rows.length;
  const meanErr2 = rows.reduce((s, r) => s + r.p2KnowErr, 0) / rows.length;
  const knowledge = {
    p1Accuracy: Math.round((1 - meanErr1 / 20) * 100),
    p2Accuracy: Math.round((1 - meanErr2 / 20) * 100),
  };

  const aligned = rows.filter((r) => r.status.key === "aligned");
  const divergent = [...rows].filter((r) => r.status.key === "divergent").sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));

  return { rows, knowledge, aligned, divergent };
}

function formatList(arr, lang) {
  const names = arr.map((r) => r.short.toLowerCase());
  if (names.length <= 1) return names[0] || "";
  const sep = L(lang, " et ", " y ");
  return names.slice(0, -1).join(", ") + sep + names[names.length - 1];
}

export function coupleSynthesis(results, p1, p2, lang = "fr") {
  const { aligned, divergent, knowledge } = results;
  const n1 = p1.name || L(lang, "Partenaire 1", "Pareja 1");
  const n2 = p2.name || L(lang, "Partenaire 2", "Pareja 2");
  const parts = [];

  if (aligned.length) {
    parts.push(
      L(
        lang,
        `Vous vivez de façon proche vos besoins de ${formatList(aligned, lang)} : ce sont des points d'accord sur lesquels vous appuyer.`,
        `Vivís de forma parecida vuestras necesidades de ${formatList(aligned, lang)}: son puntos de acuerdo en los que apoyaros.`
      )
    );
  } else {
    parts.push(
      L(
        lang,
        "Vos profils de satisfaction diffèrent sur la plupart des besoins : un dialogue attentif sera précieux.",
        "Vuestros perfiles de satisfacción difieren en la mayoría de las necesidades: un diálogo atento será valioso."
      )
    );
  }

  if (divergent.length) {
    parts.push(
      L(
        lang,
        `Les écarts les plus marqués concernent ${formatList(divergent.slice(0, 3), lang)} : ${divergent.length > 1 ? "ces besoins méritent" : "ce besoin mérite"} une écoute particulière.`,
        `Las diferencias más marcadas se refieren a ${formatList(divergent.slice(0, 3), lang)}: ${divergent.length > 1 ? "estas necesidades merecen" : "esta necesidad merece"} una escucha especial.`
      )
    );
  } else {
    parts.push(L(lang, "Aucune divergence majeure ne ressort entre vous.", "No destaca ninguna divergencia importante entre vosotros."));
  }

  const better = knowledge.p1Accuracy >= knowledge.p2Accuracy ? n1 : n2;
  parts.push(
    L(
      lang,
      `Concernant la connaissance mutuelle, ${n1} perçoit les besoins de ${n2} à ${knowledge.p1Accuracy} %, et ${n2} ceux de ${n1} à ${knowledge.p2Accuracy} % — ${better} perçoit l'autre avec le plus de justesse.`,
      `En cuanto al conocimiento mutuo, ${n1} percibe las necesidades de ${n2} en un ${knowledge.p1Accuracy} %, y ${n2} las de ${n1} en un ${knowledge.p2Accuracy} % — ${better} percibe al otro con mayor exactitud.`
    )
  );

  parts.push(
    L(
      lang,
      "Prenez le temps d'échanger sur les pistes de dialogue proposées ci-dessous, sans chercher à avoir raison mais à mieux vous comprendre.",
      "Tomaos tiempo para conversar sobre las pistas de diálogo propuestas más abajo, sin buscar tener razón sino comprenderos mejor."
    )
  );

  return parts.join(" ");
}

export function dialogueTips(results, lang = "fr") {
  const focus = results.divergent.length ? results.divergent.slice(0, 4) : results.rows.slice().sort((a, b) => Math.min(a.m1, a.m2) - Math.min(b.m1, b.m2)).slice(0, 3);
  return focus.map((r) => ({ key: r.key, short: r.short, tip: COUPLE_TIPS[lang][r.key] }));
}
